import React, { useCallback, useEffect, useState } from 'react';
import { reduxForm } from 'redux-form';
import { Box, makeStyles, Theme } from '@material-ui/core';
import SignUp from 'components/Guest/SignUp';
import { UserInformationForm } from 'components/Guest/PreJoin/UserInformationForm';
import { getExtension, isExtensionValid } from 'utils/guest-utils';
import { GUEST_JOIN } from 'constants/ScheduleModal';
import { useDispatch, useSelector } from 'react-redux';
import { EXTENSION_STATE } from 'constants/Guest';
import { useHistory } from 'react-router-dom';
import Logger from 'js-logger';
import { RootState } from 'store/reducers';
import { setExtension, setExtensionValidated } from 'reducers/guestFlowSlice';
import { lsGet, LsKey } from 'utils/localStorageUtils';

const NoExtension = React.lazy(() => import('components/Guest/NoExtension'));

const JoinForm = ({ initialize }: any) => {
  const classes = desktopStyles();
  const bypassSplash = useSelector((state: RootState) => state?.app?.guestFlow?.bypassSplash);
  const history = useHistory();
  const guestForm = useSelector((state: any) => state?.app?.form[GUEST_JOIN]?.values);
  const guestFlow = useSelector((state: RootState) => state?.app?.guestFlow);
  const [extensionValid, setExtensionValid] = useState('');
  const dispatch = useDispatch();
  const redirectToJoinForm = useCallback(
    () => history.push(`/guest/${guestForm?.extension}${bypassSplash ? 'bypassSplash=true' : ''}`),
    [history, guestForm, bypassSplash]
  );

  const checkExtension = useCallback(async () => {
    try {
      // check for extension in redux state and in url path.
      if (guestForm?.extension || getExtension()) {
        setExtensionValid(EXTENSION_STATE.VALIDATING);
        const data = await isExtensionValid(guestForm?.extension || getExtension());
        setExtensionValid(data?.isValid ? EXTENSION_STATE.VALID : EXTENSION_STATE.INVALID);
        if (data?.isValid && (!guestFlow?.extension || guestFlow.extension !== guestForm?.extension))
          dispatch(setExtension(guestForm?.extension || getExtension()));
      } else {
        setExtensionValid(EXTENSION_STATE.INVALID);
      }
      dispatch(setExtensionValidated(true));
    } catch (e) {
      Logger.error('Error fetching extension data', e);
    }
  }, [dispatch, guestFlow, guestForm]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resetReduxForm = () => {
    initialize({
      tosCheckbox: !!guestFlow?.compliance.acceptedTos || !!lsGet(LsKey.TosAccepted),
      email: guestFlow.email || lsGet(LsKey.Email),
      name: guestFlow.name || lsGet(LsKey.Name),
      extension: guestFlow.extension || getExtension(),
      employed: true
    });
  };

  useEffect(() => {
    if (extensionValid === EXTENSION_STATE.VALID) {
      redirectToJoinForm();
    }
  }, [extensionValid, redirectToJoinForm]);

  useEffect(() => {
    guestFlow.notYouClicked && resetReduxForm();
  }, [guestFlow.notYouClicked, resetReduxForm]);

  useEffect(() => {
    extensionValid !== EXTENSION_STATE.VALID && getExtension() && checkExtension();
    resetReduxForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className={classes.content}>
      {guestFlow?.extensionValidated && extensionValid === EXTENSION_STATE.VALID ? (
        <UserInformationForm />
      ) : (
        <NoExtension
          checkExtension={checkExtension}
          extensionValid={extensionValid}
          setExtensionValid={setExtensionValid}
        />
      )}
      <SignUp />
    </Box>
  );
};

const desktopStyles = makeStyles((theme: Theme) => ({
  content: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    alignItems: 'center',
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center',
    width: '100%',
    '& button': {
      maxWidth: '500px',
      width: '100%'
    }
  }
}));

export default reduxForm({
  form: GUEST_JOIN,
  getFormState: (state) => state.app.form
})(JoinForm);
